import React, { useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import classNames from 'classnames';
import _ from 'lodash';
import { reorder } from '../utils';
import { BasicTabInfo, OpenTabsType, TabTypes } from '../../../store/canvas';
import { TooltipBottom } from '../../../components/tooltips';
import { WorkflowTabIcon, CloseTabIcon } from '../assets/icons';
import { RiLoader4Line } from 'react-icons/ri';
import { SiGit } from 'react-icons/si';
import { ShowWhenTrue } from '../../../helpers';
import { Env } from '@constants/settings';
import { AddTabBtn } from '@assets/icons';
import { RootState } from '@store/types';
import { useDispatch, useSelector } from 'react-redux';
import { Select } from 'antd';
import { WorkspaceData } from '@pages/cluster_redisign/types';
import { WorkSpaceHandler } from '@api/workSpace-handler';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { getAllClusters, setWorkspaceList } from '@store/cluster';

const { Option } = Select;

interface Props {
    handlePlusIconClick?: () => any;
    openTabs: OpenTabsType;
    activeTab: BasicTabInfo['id'];
    switchTab: (arg0: BasicTabInfo) => any;
    closeTab: (arg0: BasicTabInfo) => any;
    updateOpenTabs: (arg0: OpenTabsType) => any;
    hidePlusIcon?: boolean;
    filterTabsFunc?: (arg0: TabTypes) => boolean;
    showSavedStatus: boolean
}


export const CanvasTabsComponent: React.FC<Props> = ({ filterTabsFunc, openTabs, activeTab, updateOpenTabs, switchTab, closeTab, handlePlusIconClick, children, hidePlusIcon = false, showSavedStatus = false }) => {
    // let __openTabsList = useMemo(() => openTabsList, [openTabsList]);
    const workSpaceData = useSelector((store: RootState) => store.ClusterReducer.workspaceList);
    const [loadingState, setLoadingState] = React.useState(false);
    const dispatch1 = useDispatch();
    const enabledWorkspace = workSpaceData?.filter((item) => item.isEnabled)?.[0];
    const { envVariables: Env, userDirectories } = useSelector((store:RootState)=> store.AccountReducer);
    const __openTabsList: BasicTabInfo[] = useMemo(() => {
        let __openTabsInArray = Array.from(openTabs.values());
        if(filterTabsFunc && Env?.REACT_APP_DATA_EXPLORER) {
            const _data = Env?.REACT_APP_DATA_EXPLORER
            __openTabsInArray = __openTabsInArray.filter(filterTabsFunc).filter(d => d.info.id !== _data)
        }
        return __openTabsInArray.map(tab => ({ 
            id: tab.info.id, 
            name: tab.info.name, 
            saved: tab.info.saved, 
            isLocal: (tab.info as any).isLocal,
            directoryName: userDirectories[tab.info.env]?.directories.find(directory => directory.id === tab.info.directoryId)?.name || tab.info.directoryName
        }));
    }, [openTabs, activeTab, filterTabsFunc, userDirectories]);

    function onDragEndCallback(result: any) {
        if (!result.destination) {
            return;
        }
        const updatedTabsList = reorder(__openTabsList, result.source.index,
            result.destination.index) as BasicTabInfo[];
        
        const __updatedOpenTabs: OpenTabsType = new Map();
        updatedTabsList.forEach(_tab =>{
            const tabInfo = openTabs.get(_tab.id);
            tabInfo && __updatedOpenTabs.set(_tab.id, tabInfo);
        });
        updateOpenTabs(__updatedOpenTabs);
    }

    const getItemStyle = (isDragging: any, draggableStyle: any) => ({
        // styles we need to apply on draggables
        ...draggableStyle,
    });

    const handleCloseTab = (e: React.MouseEvent<SVGSVGElement, MouseEvent>, tabInfo: BasicTabInfo) => {
        e.stopPropagation(); 
        closeTab(tabInfo);
    };

    const getAllWorkSPaces = (item: WorkspaceData) => {
        WorkSpaceHandler.GetWorkSpaceList(`${Env.REACT_APP_PLATFORM_URL}/databricks/api`,(res) => {
			dispatch1(setWorkspaceList(res));
		});
    }

    const onEnableWorkSpace = (item: WorkspaceData) => {
		const obj = {
			id: item.workspaceId,
			defaultWorkspace: !item.isEnabled,
		};
		setTimeout(() => {
			setLoadingState(true);
			if (item && item.workspaceId) {
				WorkSpaceHandler.enableWorkSpace(
					`${Env.REACT_APP_PLATFORM_URL}/databricks/api`,
					obj,
					(res) => {
                        getAllWorkSPaces(item);
						dispatch1(getAllClusters());
                        setLoadingState(false);
						successAlert("Workspace Enabled Successfully");
					},
					(e: any) => {
						setLoadingState(false);
						errorAlert(e.data.message);
					}
				);
			}
		}, 400);
	};

    return(
        <>
            <DragDropContext onDragEnd={onDragEndCallback}>
                <Droppable droppableId="droppable" direction="horizontal">
                    {(provided) => (
                        <div 
                            className="workflow__tabs__container__outer" ref={provided.innerRef}
                            {...provided.droppableProps}
                        >
                            <ul 
                                className='workflow__tabs__container'
                            >
                                {__openTabsList.map((tab: any, index: any) => {
                                    return (
                                        <Draggable key={tab.name + _.toString(index)} draggableId={`${tab.id}`} index={index}>
                                            {(provided, snapshot) => (
                                                <TooltipBottom
                                                    key={tab.name + _.toString(index)}
                                                    title={
                                                        <div className="workflowHoverCard">
                                                            <span className="workflowHoverCard__name">{tab.name}</span>
                                                            {tab.isLocal !== undefined && (
                                                                <span className="workflowHoverCard__meta">
                                                                    {tab.isLocal ? 'Private (database) version' : 'Shared (Git) version'}
                                                                </span>
                                                            )}
                                                            {tab.directoryName && (
                                                                <span className="workflowHoverCard__directory">
                                                                    <svg className="workflowHoverCard__icon" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                                                    </svg>
                                                                    <span className="workflowHoverCard__directoryName">{tab.directoryName}</span>
                                                                </span>
                                                            )}
                                                        </div>
                                                    }
                                                    modifiedOverlayClassName="workflowHoverCard__overlay"
                                                    mouseEnterDelay={0.3}
                                                >
                                                    <li
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={getItemStyle(
                                                            snapshot.isDragging,
                                                            provided.draggableProps.style
                                                        )}
                                                        className={classNames('workflow__tab', activeTab && {'workflow__tab--active' : activeTab === tab.id})}
                                                        onClick={() => switchTab(tab)}
                                                    >
                                                        <div className="workflow__tab__inner">
                                                            <div className="workflow__name__box">
                                                                {tab.isLocal === false ? (
                                                                    <SiGit fontSize={16} color="#F05033" />
                                                                ) : tab.isLocal === true ? (
                                                                    <WorkflowTabIcon />
                                                                ) : activeTab === tab.id ? (
                                                                    <RiLoader4Line fontSize={16} color="#9495A0" className="workflow__tab__loading-icon" />
                                                                ) : (
                                                                    <WorkflowTabIcon />
                                                                )}
                                                                {/* <img src="/icons/workflow_icon.svg" alt="" /> */}
                                                                <span
                                                                    id = {`workflow_tab_${tab.name.replace(/[^a-zA-Z ]/g, '')}`}
                                                                    className={'workflow__name'}
                                                                >
                                                                    {tab.name}
                                                                </span>
                                                            </div>
                                                            <div className={classNames('workflow__state', { '--saved': tab.saved, '--unsaved': !tab.saved && showSavedStatus })} />
                                                            <div className="close_tab_icon">
                                                                <CloseTabIcon
                                                                    onClick={(e) => handleCloseTab(e, tab)}
                                                                />
                                                                {/* <img
                                                                    src="/icons/close_tab.svg"
                                                                    onClick={(e) => handleCloseTab(e, tab)}
                                                                    alt=""
                                                                /> */}
                                                            </div>
                                                        </div>
                                                        <div className="hide__hoverCurve" />
                                                    </li>
                                                </TooltipBottom>
                                            )}
                                        </Draggable>
                                    );
                                })}
                                {/* <div className="workflow__add__tab">
                                    <Tooltip
                                        title={'Create Workflow'}
                                        placement="bottom"
                                    >
                                        <img
                                            src="/icons/add_tab.svg"
                                            onClick={() => {
                                                handleToggleModal('newWorkflow', true);
                                            }}

                                            alt=""
                                        />
                                    </Tooltip>
                                </div> */}
                            </ul>
                            {provided.placeholder}
                            <Select
                                placeholder="Select Workspace"
                                loading ={loadingState}
                                className='WorkspcaeSelect'
                                value={enabledWorkspace?.workspaceId || undefined} 
                                onChange={(value) => {
                                    const item = workSpaceData?.find((item) => item.workspaceId === value);
                                    onEnableWorkSpace(item as WorkspaceData);
                                }} 
                                >
                                {workSpaceData?.map(option => (
                                    <Option className='workSpaceText' key={option.workspaceId} value={option.workspaceId} style={{backgroundColor:option.isEnabled?"#666A8D":""}} ><span className='workSpaceText'>{option.workspaceName}</span></Option>
                                ))}
                            </Select>
                
                            <ShowWhenTrue show={!hidePlusIcon}>
                                <div className="jobStatusIndicator">
                                    <TooltipBottom
                                        title={'Create Workflow'}
                                        align={{offset: [0,10]}}
                                    >
                                        <div className='btn-cursor' onClick={handlePlusIconClick}>
                                            <AddTabBtn/>
                                        </div>
                                    </TooltipBottom>
                                </div>    
                            </ShowWhenTrue>
                            
                            {children}
                            
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </>
    );
};

// export const CanvasTabsComponent = memo(WrapperComponent);